import { useState, type JSX } from 'react';
import { Button, Dialog, Input, Select, Textarea } from '@artosera/ui';
import type { MomentConstat, EtatConstat } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import {
  useAjouterConstat,
  useAjouterPhoto,
  type MouvementPresente,
} from '../../api/mouvements.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import styles from './MouvementDetailPage.module.css';
export function ConstatDialog({
  galerieSlug,
  mouvement,
  onFermer,
}: {
  galerieSlug: string;
  mouvement: MouvementPresente;
  onFermer: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const ajouter = useAjouterConstat(galerieSlug);
  const photo = useAjouterPhoto(galerieSlug);
  const [oeuvre, setOeuvre] = useState(mouvement.lignes[0]?.oeuvreId ?? '');
  const [moment, setMoment] = useState<MomentConstat>('depart');
  const [etat, setEtat] = useState<EtatConstat>('bon');
  const [observations, setObservations] = useState('');
  const [par, setPar] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [erreur, setErreur] = useState('');
  const [idCree, setIdCree] = useState<string | null>(null);
  async function soumettre() {
    if (photos.length > 12) {
      setErreur(t('regie:mouvements.fiche.photosMax'));
      return;
    }
    try {
      const id =
        idCree ??
        (
          await ajouter.mutateAsync({
            id: mouvement.id,
            corps: { oeuvreId: oeuvre, moment, etatGeneral: etat, observations, etabliPar: par },
          })
        ).constat.id;
      setIdCree(id);
      for (const fichier of photos) {
        await photo.mutateAsync({ id: mouvement.id, constatId: id, fichier });
        setPhotos((v) => v.filter((f) => f !== fichier));
      }
      onFermer();
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={t('regie:mouvements.fiche.nouveauConstat')}
      closeLabel={t('commun:actions.fermer')}
    >
      <div className={styles.form}>
        <Select
          disabled={idCree !== null}
          aria-label={t('regie:mouvements.constat.oeuvre')}
          value={oeuvre}
          onValueChange={setOeuvre}
          options={mouvement.lignes.map((l) => ({ value: l.oeuvreId, label: l.titre }))}
        />
        <Select
          disabled={idCree !== null}
          aria-label={t('regie:mouvements.constat.moment')}
          value={moment}
          onValueChange={(v) => setMoment(v as MomentConstat)}
          options={(['depart', 'retour'] as const).map((v) => ({
            value: v,
            label: t(`regie:mouvements.moments.${v}`),
          }))}
        />
        <Select
          disabled={idCree !== null}
          aria-label={t('regie:mouvements.constat.etat')}
          value={etat}
          onValueChange={(v) => setEtat(v as EtatConstat)}
          options={(['bon', 'usure', 'dommage'] as const).map((v) => ({
            value: v,
            label: t(`regie:mouvements.etatsConstat.${v}`),
          }))}
        />
        <Field htmlFor="constat-notes" label={t('regie:mouvements.constat.observations')}>
          <Textarea
            id="constat-notes"
            disabled={idCree !== null}
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
          />
        </Field>
        <Field htmlFor="constat-par" label={t('regie:mouvements.constat.etabliPar')}>
          <Input
            id="constat-par"
            disabled={idCree !== null}
            value={par}
            onChange={(e) => setPar(e.target.value)}
          />
        </Field>
        <Field htmlFor="constat-photos" label={t('regie:mouvements.constat.photos')}>
          <Input
            id="constat-photos"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
        <Button
          disabled={!par.trim() || !oeuvre || ajouter.isPending || photo.isPending}
          onClick={() => void soumettre()}
        >
          {t('regie:mouvements.constat.enregistrer')}
        </Button>
      </div>
    </Dialog>
  );
}
